import { Card, Select, Button, Space, Table, message, Tabs } from 'antd';
import { http } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { PersonalReportPreview } from '../components/PersonalReportPreview';
import { PersonalReportDirectory } from '../components/PersonalReportDirectory';
import { MOCK_PERSONAL_REPORT_SUMMARY } from '../data/mockPersonalReportSummary';
import { MOCK_PERSONAL_REPORT_SUMMARY_GROWTH } from '../data/mockPersonalReportSummaryGrowth';
import { MOCK_PERSONAL_REPORT_SUMMARY_QINYANFANG } from '../data/mockPersonalReportSummaryQinyanfang';
import '../components/reportEditorial.css';

export function ReportsPage() {
  const { batches, batchId, setBatchId } = useBatches();

  /** 报表中心 Excel 下载：与后端 @Controller('reports') 路由对齐——
   *  - dept → /reports/dept-all.xlsx（整批次全部门一表）
   *  - hp   → /reports/hp.xlsx
   *  历史代码错误地拼成 /admin/reports/{kind}（不存在），导致返回 404 HTML，
   *  浏览器仍以 .xlsx 落盘，Excel 解析失败弹"对话框打开时命令无法执行"。 */
  const REPORT_PATH: Record<'dept' | 'hp', string> = {
    dept: '/reports/dept-all.xlsx',
    hp: '/reports/hp.xlsx',
  };
  const download = async (
    kind: 'dept' | 'hp',
    extra: Record<string, any> = {},
  ) => {
    if (!batchId) {
      message.warning('请先选择批次');
      return;
    }
    try {
      const r = await http.get(REPORT_PATH[kind], {
        params: { batchId, ...extra },
        responseType: 'blob',
      });
      const ct = String(r.headers?.['content-type'] ?? '');
      if (!ct.includes('spreadsheetml')) {
        let text = '';
        try {
          text = await (r.data as Blob).text();
        } catch {
          /* ignore */
        }
        message.error(`后端未返回 Excel：${text.slice(0, 200) || '未知错误'}`);
        return;
      }
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}_${batchId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('已下载');
    } catch (e: any) {
      message.error(`下载失败：${e?.response?.status ?? ''} ${e?.message ?? '未知错误'}`);
    }
  };

  const downloadSamplePdf = async () => {
    try {
      const r = await http.get('/reports/personal/sample.pdf', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'personal-sample-DEMO.pdf';
      a.click();
      URL.revokeObjectURL(url);
      message.success('示例 PDF 已下载');
    } catch (e: any) {
      message.error(`下载失败：${e?.response?.status ?? ''} ${e?.message ?? '未知错误'}`);
    }
  };

  return (
    <div className="report-editorial">
      <div className="tg-page-title">报表中心</div>
      <section className="report-context-bar">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Select
            placeholder="选择盘点批次"
            style={{ width: 240 }}
            value={batchId}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
        </Space>
      </section>

      <Tabs
        defaultActiveKey="personal"
        size="large"
        items={[
          {
            key: 'personal',
            label: '个人报告',
            children: <PersonalReportDirectory batchId={batchId ?? undefined} />,
          },
          {
            key: 'export',
            label: '汇总导出',
            children: (
              <Card>
                <Table
                  rowKey="key"
                  dataSource={[
                    { key: 'dept', name: '部门盘点报告（整批次全部门一表 · 含九宫格 / 胜任力 / 高潜结论）', kind: 'dept' as const },
                    { key: 'hp', name: '全公司高潜清单', kind: 'hp' as const },
                  ]}
                  columns={[
                    { title: '类型', dataIndex: 'name' },
                    {
                      title: '操作',
                      width: 160,
                      render: (_, r) => (
                        <Button disabled={!batchId} onClick={() => download(r.kind)}>
                          导出 Excel
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'samples',
            label: '模板示例',
            children: (
              <Card
                title="个人盘点报告 · 屏幕示例"
                extra={
                  <Button size="small" type="primary" onClick={() => downloadSamplePdf()}>
                    下载示例 PDF
                  </Button>
                }
              >
                <Tabs
                  defaultActiveKey="qin"
                  items={[
                    {
                      key: 'qin',
                      label: '秦艳芳 · 生产快照',
                      children: (
                        <PersonalReportPreview
                          variant="sample"
                          data={MOCK_PERSONAL_REPORT_SUMMARY_QINYANFANG}
                          titleSuffix="2026 示例预览 · 素质雷达叠加（示意分）"
                        />
                      ),
                    },
                    {
                      key: 'hh',
                      label: '李示例 · HH 标杆',
                      children: (
                        <PersonalReportPreview
                          variant="sample"
                          data={MOCK_PERSONAL_REPORT_SUMMARY}
                          titleSuffix="2026 示例预览 · HH 标杆"
                        />
                      ),
                    },
                    {
                      key: 'ml',
                      label: '张拓展 · ML 待发展',
                      children: (
                        <PersonalReportPreview
                          variant="sample"
                          data={MOCK_PERSONAL_REPORT_SUMMARY_GROWTH}
                          titleSuffix="2026 示例预览 · 待发展"
                        />
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
